// Staged build-progress strip for Forecast Studio: replaces the generic
// package-build notice with stage/progress/ETA when the ensure endpoint
// provides them, and renders failure guidance with recovery actions.
import { formatAsOf, trimStr } from './cfoFormat'
import {
  FORECAST_BUILD_STAGES,
  forecastBuildFailureGuidance,
  type ForecastBuildFailureActionKind,
  type ForecastStudioPackageBuildState,
} from './forecastBuildState'

export type CfoForecastBuildProgressProps = {
  build: ForecastStudioPackageBuildState
  priorForecastLabel?: string | null
  beyondBuiltCoverage?: boolean
  canPickPriorMonth?: boolean
  onAction?: (kind: ForecastBuildFailureActionKind) => void
}

const etaLabel = (etaSeconds: number | null | undefined): string => {
  if (etaSeconds == null || !Number.isFinite(etaSeconds)) return ''
  const seconds = Math.max(0, Math.round(etaSeconds))
  if (seconds < 90) return `about ${seconds}s remaining`
  return `about ${Math.round(seconds / 60)} min remaining`
}

export function CfoForecastBuildProgress({
  build,
  priorForecastLabel = null,
  beyondBuiltCoverage = false,
  canPickPriorMonth = true,
  onAction,
}: CfoForecastBuildProgressProps) {
  const building = build.status === 'queued' || build.status === 'running'
  const monthLabel = trimStr(build.asOfDate) ? formatAsOf(build.asOfDate) : 'The selected month'
  if (building) {
    const activeStageIndex = build.stage ? FORECAST_BUILD_STAGES.findIndex((entry) => entry.stage === build.stage) : -1
    const eta = etaLabel(build.etaSeconds)
    return (
      <div className="cfo-shell-notice cfo-forecast-build-progress" role="status" aria-live="polite">
        <div className="cfo-forecast-build-progress-main">
          <b>
            {beyondBuiltCoverage
              ? `${monthLabel} isn't covered yet — building it now.`
              : build.status === 'running' ? 'Forecast package building.' : 'Forecast package queued.'}
          </b>
          <span>{trimStr(build.userMessage) || build.message}</span>
          {priorForecastLabel ? (
            <span className="cfo-forecast-build-progress-prior">
              Showing the {priorForecastLabel} forecast while {monthLabel} builds…
            </span>
          ) : null}
          {activeStageIndex >= 0 ? (
            <ol className="cfo-forecast-build-progress-stages" aria-label="Build stages">
              {FORECAST_BUILD_STAGES.map((entry, index) => (
                <li
                  key={entry.stage}
                  data-state={index < activeStageIndex ? 'done' : index === activeStageIndex ? 'active' : 'pending'}
                >
                  {entry.label}
                </li>
              ))}
            </ol>
          ) : null}
          {build.progressPct != null ? (
            <div
              className="cfo-forecast-build-progress-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(build.progressPct)}
            >
              <span style={{ width: `${Math.round(build.progressPct)}%` }} />
            </div>
          ) : null}
          {eta ? <span className="cfo-forecast-build-progress-eta">{eta}</span> : null}
          {build.nextSteps?.length ? (
            <ul className="cfo-forecast-build-progress-steps">
              {build.nextSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    )
  }
  const guidance = forecastBuildFailureGuidance(build.status, build.validation, build.asOfDate)
  if (!guidance) return null
  const actions = guidance.actions.filter((action) => action.kind !== 'pick_prior_month' || canPickPriorMonth)
  return (
    <div className="cfo-shell-notice error cfo-forecast-build-progress" role="status" aria-live="polite">
      <div className="cfo-forecast-build-progress-main">
        <b>{guidance.message}</b>
        {trimStr(build.userMessage) ? <span>{build.userMessage}</span> : null}
        {priorForecastLabel ? (
          <span className="cfo-forecast-build-progress-prior">
            The {priorForecastLabel} forecast below is unchanged.
          </span>
        ) : null}
      </div>
      {onAction && actions.length ? (
        <div className="cfo-forecast-build-progress-actions">
          {actions.map((action) => (
            <button key={action.kind} type="button" className="btn" onClick={() => onAction(action.kind)}>
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
